import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { Badge, Box, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Tooltip } from '@chakra-ui/react'
import { MdCallSplit, MdCheckCircle, MdOutlineSubdirectoryArrowRight, MdRemoveCircle } from 'react-icons/md'

import CostBudget from './CostBudget'

//Constant

import { RoleInOffer } from '@/features/offers/constants'

//Helper
import {
  CostBudgetWorkflowInitiatorType,
  CostBudgetWorkflowStatus,
  SourcingDecisionWorkflowStatus,
} from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import { getCostBudgetTabControls } from '@/features/offers/offer-detail/helper/sourcing.helper'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import { showSplitCostBudgetModal } from '@/features/offers/slices/offerModal.slice'
import { selectPrintCapture } from '@/stores/offers/printOfferModal'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { isNullish } from '@/utils/helper'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

/**
 * An icon action inside a cost budget tab.
 *
 * Rendered as a focusable `role="button"` rather than a real button because it
 * sits inside a `Tab`, and clicks are stopped from bubbling so acting on one
 * budget does not also switch the selected tab.
 */
const TabIconAction = ({ label, color, hoverColor, onActivate, children }) => {
  const activate = (event) => {
    event.stopPropagation()
    onActivate()
  }

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    activate(event)
  }

  return (
    <Tooltip label={label}>
      <Text
        role="button"
        tabIndex={0}
        aria-label={label}
        cursor="pointer"
        color={color}
        _hover={{ color: hoverColor }}
        onClick={activate}
        onKeyDown={handleKeyDown}
      >
        {children}
      </Text>
    </Tooltip>
  )
}

const renderCostBudgetStatusBadge = (costBudget, isProcurementSide = false) => {
  if (!costBudget?.detail?.isActive) {
    const inactiveReason = costBudget?.detail?.inactiveReason ?? '(-)'
    return (
      <Tooltip label={`Reason: ${inactiveReason}`}>
        <Badge>Inactive</Badge>
      </Tooltip>
    )
  }

  if (costBudget?.detail?.workflow?.status === CostBudgetWorkflowStatus.OPEN) {
    const issues = costBudget?.detail?.workflow?.issues
    if (!Array.isArray(issues) || issues.length === 0) {
      return <></>
    }

    const lastIssue = issues[issues.length - 1]
    const pendingReplyUserType = lastIssue.pendingReplyUserType

    if (
      (pendingReplyUserType === CostBudgetWorkflowInitiatorType.PROCUREMENT && isProcurementSide) ||
      (pendingReplyUserType === CostBudgetWorkflowInitiatorType.TECHNICAL && !isProcurementSide)
    ) {
      return <Badge colorScheme="red">{SourcingDecisionWorkflowStatus.ACTION_REQUIRED}</Badge>
    }
    return <Badge colorScheme="green">{SourcingDecisionWorkflowStatus.UPCOMING}</Badge>
  }

  return <></>
}

const CostBudgetTabs = ({
  costBudgets = [],
  costBudgetIndices = [],
  removeCostBudget = () => {},
  canEditCostBudget = false,
  canRemoveCostBudget = false,
  hasSelectedSubSystem = false,
}) => {
  const { role } = useSelector(selectOfferDetail)

  const [searchParams, setSearchParams] = useSearchParams()
  const costBudgetIndex = searchParams.get('costBudgetIndex')
  const submissionIndex = searchParams.get('submissionIndex')
  const costBudgetId = searchParams.get('costBudgetId')

  const [costBudgetTabIndex, setCostBudgetTabIndex] = useState(0)
  const costBudgetRef = useRef([])

  // The print modal drives this nested tab strip too, since Sourcing's cost budgets are a
  // second, independent set of tabs — switching the outer Sourcing tab alone only reveals
  // whichever single cost budget happens to already be selected.
  const { isActive: isPrintCaptureMode, targetCostBudgetIndex: printCaptureTargetCostBudgetIndex } =
    useSelector(selectPrintCapture)

  useEffect(() => {
    if (printCaptureTargetCostBudgetIndex !== null && printCaptureTargetCostBudgetIndex !== costBudgetTabIndex) {
      setCostBudgetTabIndex(printCaptureTargetCostBudgetIndex)
    }
  }, [printCaptureTargetCostBudgetIndex, costBudgetTabIndex])

  const bookingCreateDate = useWatch({ name: `booking.bookingCreateDate` })

  const { open: openConfirmationModal } = useConfirmationModalManagement()
  const { useCreateOfferCostBudget, useToggleActiveOfferCostBudget } = useOfferCostBudget()
  const toggleActiveOfferCostBudget = useToggleActiveOfferCostBudget()

  const { getValues, setValue } = useFormContext()
  const dispatch = useDispatch()
  //Translated Variable

  const indexedCostBudgets = costBudgets.map((costBudget, index) => ({
    costBudget,
    originalIndex: isNullish(costBudgetIndices[index]) ? index : costBudgetIndices[index],
  }))

  useEffect(() => {
    if (!isNullish(submissionIndex) && !isNullish(costBudgetIndex)) {
      const displayIndex = indexedCostBudgets.findIndex(
        (item) => String(item.originalIndex) === String(costBudgetIndex)
      )
      const targetIndex = displayIndex > -1 ? displayIndex : Number(costBudgetIndex)
      const el = costBudgetRef.current[targetIndex]
      if (el) {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: 'smooth',
        })
      }
    }
  }, [indexedCostBudgets, submissionIndex, costBudgetIndex])

  useEffect(() => {
    if (costBudgetId) {
      const index = indexedCostBudgets.findIndex((item) => item.costBudget.detail.id === costBudgetId)
      if (index > -1) {
        setCostBudgetTabIndex(index)

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('costBudgetId')
        setSearchParams(nextParams, { replace: true })
      }
    }
  }, [costBudgetId, indexedCostBudgets, searchParams, setSearchParams])

  useEffect(() => {
    if (!isNullish(costBudgetIndex)) {
      const indexFromQuery = indexedCostBudgets.findIndex(
        (item) => String(item.originalIndex) === String(costBudgetIndex)
      )
      if (indexFromQuery > -1) {
        setCostBudgetTabIndex(indexFromQuery)

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('costBudgetIndex')
        setSearchParams(nextParams, { replace: true })
      }
    }
  }, [costBudgetIndex, indexedCostBudgets, searchParams, setSearchParams])

  useEffect(() => {
    if (costBudgetTabIndex > indexedCostBudgets.length - 1) {
      setCostBudgetTabIndex(0)
    }
  }, [costBudgetTabIndex, indexedCostBudgets.length])

  const handleRemoveCostBudget = useCallback(
    async (originalIndex) => {
      try {
        const costBudget = getValues(`sourcing.costBudgets.${originalIndex}`)

        if (!costBudget) {
          throw new Error('[Unexpected Error] Missing Cost Budget')
        }

        const costBudgetId = costBudget?.detail?._id

        if (isNullish(costBudgetId)) {
          throw new Error('[Unexpected Error] Missing Cost Budget Id', costBudget?.detail)
        }

        if (costBudgetId === '') {
          //Not yet saved, just remove
          removeCostBudget(originalIndex)
          //Select the first
          setCostBudgetTabIndex(0)
          return
        }

        const { isConfirmed, inputValue } = await openConfirmationModal({
          title: 'Inactivate Cost Budget',
          isInput: true,
          inputLabel: 'Reason',
          message: 'Are you sure you want to inactivate this cost budget? It can be activated again later.',
        })

        if (!isConfirmed) {
          return
        }

        if (inputValue === '') {
          throw new Error('Please enter reason ')
        }

        await toggleActiveOfferCostBudget.mutateAsync({ id: costBudgetId, reason: inputValue })

        //The offer query is never invalidated by this mutation, so reflect the new state on the form directly.
        setValue(`sourcing.costBudgets.${originalIndex}.detail.isActive`, false, { shouldDirty: false })
      } catch (err) {
        console.error(err)
        //Server errors are already surfaced by the axios response interceptor.
        if (!err.response) {
          toast.error(err.message)
        }
      }
    },
    [getValues, openConfirmationModal, removeCostBudget, setValue, toggleActiveOfferCostBudget]
  )

  const handleActivateCostBudget = useCallback(
    async (originalIndex) => {
      try {
        const costBudget = getValues(`sourcing.costBudgets.${originalIndex}`)

        if (!costBudget) {
          throw new Error('[Unexpected Error] Missing Cost Budget')
        }

        const costBudgetId = costBudget?.detail?._id

        if (isNullish(costBudgetId) || costBudgetId === '') {
          throw new Error('[Unexpected Error] Missing Cost Budget Id')
        }

        const { isConfirmed } = await openConfirmationModal({
          title: 'Activate Cost Budget',
          message: 'Are you sure you want to activate this cost budget?',
        })

        if (!isConfirmed) {
          return
        }

        await toggleActiveOfferCostBudget.mutateAsync({ id: costBudgetId })

        setValue(`sourcing.costBudgets.${originalIndex}.detail.isActive`, true, { shouldDirty: false })
      } catch (err) {
        console.error(err)
        if (!err.response) {
          toast.error(err.message)
        }
      }
    },
    [getValues, openConfirmationModal, setValue, toggleActiveOfferCostBudget]
  )

  const handleSplitCostBudget = (index) => {
    const costBudget = getValues(`sourcing.costBudgets.${index}`)
    const offerId = getValues('_id')
    dispatch(
      showSplitCostBudgetModal({
        originalCostBudgetName: costBudget?.detail?.purpose ?? '',
        originalCostBudgetRFQCode: costBudget?.detail?.RFQCode ?? '',
        offerId: offerId,
        costBudgetIndex: index,
      })
    )
  }

  return (
    <Tabs
      size="md"
      variant="enclosed"
      index={costBudgetTabIndex}
      orientation="vertical"
      isLazy
      onChange={(index) => {
        setCostBudgetTabIndex(index)
      }}
    >
      <Flex w="100%">
        <TabList
          overflowX={'scroll'}
          overflowY={'hidden'}
          sx={{
            '::-webkit-scrollbar': {
              display: 'none',
            },
          }}
          w="30%"
          minW="300px"
        >
          {indexedCostBudgets.map((item, index) => {
            const { costBudget, originalIndex } = item
            const isAfterBooking =
              bookingCreateDate && moment(bookingCreateDate).isBefore(moment(costBudget?.detail?.createdAt))

            const displayName =
              costBudget?.detail?.purpose === '' ? `Cost Budget ${index + 1}` : costBudget?.detail?.purpose
            const RFQCode = costBudget?.detail?.RFQCode ? ` ${costBudget.detail.RFQCode}` : ''

            const toolTipLabel = `${displayName}, Created ${isAfterBooking ? 'after' : 'before'} booking, at ${moment(costBudget.createdAt).format('YYYY-MM-DD HH:mm:ss')}`

            const { showSplit, showInactivate, showActivate } = getCostBudgetTabControls(costBudget, {
              canEditCostBudget,
              canRemoveCostBudget,
            })
            return (
              <Tab
                key={'costBudgetTab_' + index}
                as={Flex}
                gap={2}
                direction="column"
                alignItems={'start'}
                borderTopEndRadius={0}
                borderRight={'0'}
                _selected={{
                  borderColor: 'teal.500',
                  borderWidth: '2px',
                }}
              >
                <Flex
                  w="100%"
                  borderLeft={isAfterBooking ? '3px solid ' : '3px solid '}
                  borderColor={isAfterBooking ? 'green.400' : 'blue.400'}
                >
                  {costBudget?.splitForm && (
                    <Box mr="2" flexShrink={0}>
                      <MdOutlineSubdirectoryArrowRight size={'20px'} />
                    </Box>
                  )}
                  <Tooltip label={toolTipLabel}>
                    <Flex direction={'column'} gap={1} w={'100%'} minW={0} flex={1} overflow={'hidden'} pl={1}>
                      <Text
                        fontWeight={'normal'}
                        textDecoration={'none'}
                        textDecorationThickness={'3px'}
                        textDecorationColor={'teal.400'}
                        textUnderlineOffset={'5px'}
                        w={'100%'}
                        minW={0}
                        textOverflow={'ellipsis'}
                        whiteSpace={'nowrap'}
                        overflow={'hidden'}
                      >
                        {displayName}
                        <br />
                      </Text>
                      <Flex>
                        <Text fontSize="0.75rem" color="gray.500">
                          {RFQCode}
                        </Text>
                      </Flex>
                    </Flex>
                  </Tooltip>

                  {!isPrintCaptureMode && showSplit && (
                    <TabIconAction
                      color="teal.500"
                      hoverColor="teal.300"
                      onActivate={() => handleSplitCostBudget(originalIndex)}
                    >
                      <MdCallSplit size={'1.2em'} />
                    </TabIconAction>
                  )}

                  {!isPrintCaptureMode && showInactivate && (
                    <TabIconAction
                      label="Inactivate cost budget"
                      color="red.500"
                      hoverColor="red.300"
                      onActivate={() => handleRemoveCostBudget(originalIndex)}
                    >
                      <MdRemoveCircle size={'1.2em'} />
                    </TabIconAction>
                  )}
                  {showActivate && (
                    <TabIconAction
                      label="Activate cost budget"
                      color="green.500"
                      hoverColor="green.300"
                      onActivate={() => handleActivateCostBudget(originalIndex)}
                    >
                      <MdCheckCircle size={'1.2em'} />
                    </TabIconAction>
                  )}
                </Flex>
                {renderCostBudgetStatusBadge(costBudget, role === RoleInOffer.PROCUREMENT_OFFICER)}
              </Tab>
            )
          })}
        </TabList>

        <TabPanels id="offerSourcingCostBudgetTabPanels">
          {indexedCostBudgets.map((item, index) => (
            <TabPanel
              key={item.costBudget.id}
              border="1px solid rgb(226, 232, 240)"
              borderColor={''}
              overflowY={'auto'}
              w="calc(100% - 300px)"
              ref={(el) => (costBudgetRef.current[index] = el)}
            >
              <CostBudget costBudgetIndex={item.originalIndex} hasSelectedSubSystem={hasSelectedSubSystem} />
            </TabPanel>
          ))}
        </TabPanels>
      </Flex>
    </Tabs>
  )
}

export default CostBudgetTabs
